import { useCustomerViewPage } from './CustomerView.hooks';
import {
  BaseDialog,
  Button,
  CheckSelectField,
  HookedForm,
  InputField,
  PhoneField,
  Tabs,
  TextField,
} from '@app/components';
import { CustomerTabs } from '@app/utils/consts';
import { ListPill, TagPill } from '@app/components/CustomerTable/CustomerTable';
import { ListInterface, OptionInterface, TagInterface } from '@app/models/util';
import UserIcon from '@app/assets/icons/user.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import PencilIcon from '@app/assets/icons/pencil.svg?react';
import { useEffect } from 'react';
import { usePermissions } from '@app/contexts/PermissionContext.tsx';

export const CustomerView = () => {
  const {
    navigate,
    customerData,
    customerStatistics,
    customerOpenCounts,
    handleTabChange,
    customerUpdateForm,
    handleCustomerUpdate,
    customerUpdateModal,
    handleCustomerModal,
    isPending,
    listsAndTags,
    invalidFields,
    setInvalidFields,
  } = useCustomerViewPage();
  const getEmailStats = () => {
    if (!customerStatistics?.data?.statistics?.email) {
      return [
        { count: 0, name: 'Sent', percent: 0 },
        { count: 0, name: 'Opened', percent: 0 },
        { count: 0, name: 'Clicked', percent: 0 },
        { count: 0, name: 'Rated', percent: 0 },
        { count: 0, name: 'Feedback', percent: 0 },
        { count: 0, name: 'Unsubscribed', percent: 0 },
      ];
    }

    const stats = customerStatistics.data.statistics.email;
    const openCounts = customerOpenCounts?.data;
    
    return [
      { count: stats.sent || 0, name: 'Sent', percent: 0 },
      { count: openCounts?.openedEmail || 0, name: 'Clicked', percent: 0 },
      { count: stats.rated || 0, name: 'Rated', percent: 0 },
      { count: stats.feedback || 0, name: 'Feedback', percent: 0 },
      { count: stats.unsubscribed || 0, name: 'Unsubscribed', percent: 0 },
    ];
  };

  const getSmsStats = () => {
    if (!customerStatistics?.data?.statistics?.sms) {
      return [
        { count: 0, name: 'Sent', percent: 0 },
        { count: 0, name: 'Rated', percent: 0 },
        { count: 0, name: 'Feedback', percent: 0 },
        { count: 0, name: 'Unsubscribed', percent: 0 },
      ];
    }

    const stats = customerStatistics.data.statistics.sms;
    const openCounts = customerOpenCounts?.data;
    
    return [
      { count: stats.sent || 0, name: 'Sent', percent: 0 },
      { count: openCounts?.openedSms || 0, name: 'Clicked', percent: 0 },
      { count: stats.rated || 0, name: 'Rated', percent: 0 },
      { count: stats.feedback || 0, name: 'Feedback', percent: 0 },
      { count: stats.unsubscribed || 0, name: 'Unsubscribed', percent: 0 },
    ];
  };

  useEffect(() => {
    const subscription = customerUpdateForm.watch((values: any) => {
      const email = values.email || '';
      const emailRegex = /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
      
      setInvalidFields({
        email: !email || !emailRegex.test(email) || email.length > 100,
        firstName: !values.firstName.trim(),
        lastName: !values.lastName.trim(),
      });
    });
    return () => subscription.unsubscribe();
  }, [customerUpdateForm]);
  const {can} = usePermissions()

  return (
    <>
      <div>
        <div className="bg-white border-b border-b-base-ghostWhite ">
          <div className="container mx-auto">
            <div className="flex justify-between items-center p-4">
              <h2 className="font-bold text-[28px]">
                <button
                  onClick={() => navigate('/customers/contacts')}
                  className="mr-2"
                >
                  <ArrowBackIcon />
                </button>
                {customerData?.data?.customer?.firstName}{' '}
                {customerData?.data?.customer?.lastName}
              </h2>
            </div>
            <div className="px-4">
              <Tabs
                tabs={CustomerTabs as unknown as OptionInterface[]}
                onClick={handleTabChange}
              />
            </div>
          </div>
        </div>
        <div className="container mx-auto p-4">
          <div className="grid grid-cols-1 md:grid-cols-8 gap-4 p-4">
            <div className="bg-white p-4 h-fit col-span-5 md:col-span-3 w-full">
              <div className="flex items-center justify-between">
                <h5 className="font-bold text-sm">Contact Information</h5>
                { can('editCustomers') &&
                <button
                  onClick={handleCustomerModal}
                  className="bg-base-antiFlash p-2 rounded-lg"
                >
                  <PencilIcon />
                </button>
                }
              </div>
              <hr className="my-4" />
              <div className="space-y-4">
                <table>
                  <tbody>
                    <tr>
                      <td className="w-[130px]">
                        <span className="text-xs text-base-coolGray">
                          ECHO ID
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {customerData?.data?.customer?.id}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[130px]">
                        <span className="text-xs text-base-coolGray">
                          FIRST NAME
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {customerData?.data?.customer?.firstName}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[130px]">
                        <span className="text-xs text-base-coolGray">
                          LAST NAME
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {customerData?.data?.customer?.lastName}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[130px]">
                        <span className="text-xs text-base-coolGray">
                          EMAIL
                        </span>
                      </td>
                      <td>
                        <a href={`mailto:${customerData?.data?.customer?.email}`} className="text-sm font-medium hover:text-brand-baseBlue break-all">
                          {customerData?.data?.customer?.email}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td className="w-[130px]">
                        <span className="text-xs text-base-coolGray">
                          PHONE NUMBER
                        </span>
                      </td>
                      <td>
                        <span className="text-sm font-medium">
                          {customerData?.data?.customer?.phoneNumber}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr className="my-6" />
                <div>
                  <h5 className="text-xs text-base-coolGray mb-2">LISTS</h5>
                  <div className="flex gap-2 flex-wrap">
                    {customerData?.data?.customer?.lists?.map(
                      (list: ListInterface) => (
                        <ListPill key={list.name} name={list.name} />
                      )
                    )}
                  </div>
                </div>
                <hr className="my-6" />
                <div>
                  <h5 className="text-xs text-base-coolGray mb-2">TAGS</h5>
                  <div className="flex gap-2 flex-wrap max-h-[150px] overflow-y-auto">
                    {customerData?.data?.customer?.tags?.map(
                      (tag: TagInterface) => (
                        <TagPill key={tag.name} name={tag.name} />
                      )
                    )}
                  </div>
                </div>
                <hr className="my-6" />
                <div>
                  <h5 className="text-xs text-base-coolGray mb-2">NOTES</h5>
                  <p className="text-base-coolGray text-sm break-words whitespace-pre-wrap overflow-hidden">
                    {customerData?.data?.customer?.notes}
                  </p>
                </div>
                <hr className="my-6" />
                <div>
                  <h5 className="text-xs text-base-coolGray mb-2">SOURCES</h5>
                  <p className="text-base-coolGray text-sm">LOCAL</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 col-span-5 h-fit">
              <div>
                <h5 className="font-bold text-sm">
                  <div className="flex items-center justify-between">
                    <h5 className="font-bold text-sm py-2">Overview</h5>
                  </div>
                  <hr className="my-4" />
                  <div>
                    <h5 className="text-xs text-base-coolGray mb-2">
                      EMAIL CAMPAIGNS
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getEmailStats().map((item) => (
                        <div
                          key={item.name}
                          className="bg-base-seasalt rounded-lg p-4 flex gap-2 flex-col"
                        >
                          <span className="text-2xl font-bold">
                            {item.count}
                          </span>
                          <p className="text-base-coolGray text-xs font-medium">
                            {item.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <hr className="my-6" />
                  <div>
                    <h5 className="text-xs text-base-coolGray mb-2">
                      SMS CAMPAIGNS
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {getSmsStats().map((item) => (
                        <div
                          key={item.name}
                          className="bg-base-seasalt rounded-lg p-4 flex gap-2 flex-col"
                        >
                          <span className="text-2xl font-bold">
                            {item.count}
                          </span>
                          <p className="text-base-coolGray text-xs font-medium">
                            {item.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </h5>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BaseDialog
        size="max-w-2xl"
        open={customerUpdateModal}
        onClose={handleCustomerModal}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div></div>
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <UserIcon width={24} height={24} />
          </div>
          <div className="mr-auto">
            <h5 className="font-bold text-lg">Update customer</h5>
            <p className="text-base-coolGray text-sm">
              Fill in the fields below and updated selected customer
            </p>
          </div>
          <div>
            <button type="button" onClick={handleCustomerModal}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <HookedForm
            formHook={customerUpdateForm}
            onSubmit={handleCustomerUpdate}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <InputField name="firstName" label="First name" />
                {invalidFields.firstName && (
                  <span className="text-xs text-red-500">
                    First name must be at least 1 character
                  </span>
                )}
              </div>
              <div className="col-span-2 sm:col-span-1">
                <InputField name="lastName" label="Last name" />
                {invalidFields.lastName && (
                  <span className="text-xs text-red-500">
                    Last name must be at least 1 character
                  </span>
                )}
              </div>
              <div className="col-span-2 flex gap-4">
                <div className="w-1/2">
                  <InputField name="email" label="Email" />
                  {invalidFields.email && (
                    <span className="text-xs text-red-500">
                      {invalidFields.email ? 'Invalid email format' : 'Email must be 100 characters or less'}
                    </span>
                  )}
                </div>
                <div className="w-1/2">
                  <PhoneField
                    name="phoneNumber"
                    isOptional
                    label="Phone Number"
                  />
                </div>
              </div>
              <div className="col-span-2 flex gap-4">
                <div className="w-1/2">
                  <CheckSelectField
                    name="lists"
                    label="Add to list(s)"
                    isOptional
                    options={listsAndTags?.lists?.map(
                      (item: ListInterface) => ({
                        label: item.name,
                        value: item.id,
                      })
                    )}
                  />
                </div>
                <div className="w-1/2">
                  <CheckSelectField
                    name="tags"
                    label="Add tag(s)"
                    isOptional
                    options={listsAndTags?.tags?.map((item: TagInterface) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </div>
              </div>
            </div>
            <TextField name="notes" label="Notes" isOptional />
            <div className="flex items-center gap-4 justify-center ">
              <Button
                kind="secondary"
                className="w-full"
                type="button"
                onClick={handleCustomerModal}
              >
                Cancel
              </Button>
              <Button
                disabled={isPending}
                loading={isPending}
                className="w-full"
                type="submit"
              >
                Update customer
              </Button>
            </div>
          </HookedForm>
        </div>
      </BaseDialog>
    </>
  );
};
